import type { LoanRequestBodyType } from '@repo/api/endpoints/loan';
import type { LoanStage } from '@repo/shared/types';

import { useMainHubStoreActions } from '@/store/main-hub.store';
import { VIEW_TYPE } from '@/store/view.type';

import type { AllViewsOptions } from '../store/loan-process.store';
import { useLoanProcessStore } from '../store/loan-process.store';

/**
 * Stages are defined by P24
 * 0 - no loan flow started
 * 1 - loan application sent
 * 2 - loan application granted
 * 3 - agreement accepted
 * 4 - sms code sent
 * 5 - loan flow successfully completed
 * 6 - application overdue
 * 7 - loan application negative
 * 8 - too many attempts to sign the contract
 * 9 - application cancelled by merchant
 */

interface LoanStage2DataAgreementAndApprovals {
  viewOptions: {
    stageViewType: 'APPLICATION_POSITIVE';
  };
}

interface LoanStage2DataApplicationPositive {
  viewOptions: {
    stageViewType: 'AGREEMENT_AND_APPROVALS';
    AGREEMENT_AND_APPROVALS: AllViewsOptions['AGREEMENT_AND_APPROVALS'];
  };
}

type LoanStage2Data = LoanStage2DataAgreementAndApprovals | LoanStage2DataApplicationPositive;

export function useLoanProcessModal() {
  const {
    actions: { setCurrentStage, setLoanLimit, setLoanData, setLoanSMSIds },
  } = useLoanProcessStore();

  const { getPendingLoan, getLoansLimit, setCurrentView } = useMainHubStoreActions();

  function setLoanStage(
    stage: 0,
    data: {
      applicationFormData: LoanRequestBodyType;
    },
  ): void;
  function setLoanStage(stage: 1): void;
  function setLoanStage(stage: 2, data: LoanStage2Data): void;
  function setLoanStage(stage: 3): void;
  function setLoanStage(stage: 4): void;
  function setLoanStage(stage: 5): void;
  function setLoanStage(stage: 7): void;
  function setLoanStage(stage: 8): void;
  function setLoanStage(
    stage: LoanStage,
    data?: {
      applicationFormData?: LoanRequestBodyType;
      viewOptions?: Partial<AllViewsOptions>;
    },
  ): void {
    setCurrentStage(stage, data);
  }

  function openLoanModalOnCurrentLoanStage(omitAmountChangeView?: boolean) {
    const loan = getPendingLoan();
    const loanLimit = getLoansLimit();
    if (!loanLimit || !loan) {
      setCurrentView(VIEW_TYPE.SERVICE_NOT_AVAILABLE);

      return;
    }
    setLoanData({
      amount: loan.amount,
      documents: loan.documents,
      id: loan.id,
      loanRepaymentPrice: loan.loanRepaymentPrice,
      period: loan.period,
      pricing: loan.pricing,
      loanAmountChange: loan.loanAmountChange,
      provider: loan.provider,
    });
    setLoanLimit(loanLimit);
    setLoanSMSIds(loan.sms);
    const { stage } = loan;
    // This is necessary because of the way the function is called.
    // An overloaded function requires that you specify a specific stage
    if (stage === 1) {
      setLoanStage(stage);
    }
    if (stage === 2) {
      let data: LoanStage2Data;
      if (loan.loanAmountChange && !omitAmountChangeView) {
        data = {
          viewOptions: {
            stageViewType: 'APPLICATION_POSITIVE',
          },
        };
      } else {
        data = {
          viewOptions: {
            stageViewType: 'AGREEMENT_AND_APPROVALS',
            AGREEMENT_AND_APPROVALS: {
              openInEditMode: false,
            },
          },
        };
      }

      setLoanStage(stage, data);
    }
    if (stage === 3) {
      setLoanStage(stage);
    }
    if (stage === 4) {
      setLoanStage(stage);
    }
    if (stage === 7) {
      setLoanStage(stage);
    }
    if (stage === 8) {
      setLoanStage(stage);
    }
  }

  return {
    setLoanStage,
    openLoanModalOnCurrentLoanStage,
  };
}
